import { useCallback, useRef, useState } from 'react';

const getVisibleSlidesCount = swiper => {
  if (typeof swiper?.slidesPerViewDynamic !== 'function') {
    return 1;
  }

  try {
    const visibleSlidesCount = swiper.slidesPerViewDynamic();

    return Number.isFinite(visibleSlidesCount) && visibleSlidesCount > 0 ? visibleSlidesCount : 1;
  } catch {
    return 1;
  }
};

const useBonusSliderAtEnd = resetKey => {
  const [atEnd, setAtEnd] = useState(false);
  const swiperRef = useRef(null);
  const pendingSlideRequestRef = useRef(null);
  const targetSlideRef = useRef(null);
  const resetKeyRef = useRef(resetKey);

  if (resetKeyRef.current !== resetKey) {
    resetKeyRef.current = resetKey;
    swiperRef.current = null;
    pendingSlideRequestRef.current = null;
  }

  const syncSwiperAtEnd = useCallback(swiper => {
    setAtEnd(Boolean(swiper?.isEnd));
  }, []);

  const scrollToSlide = useCallback(
    (index, { force = false } = {}) => {
      if (index == null || index < 0) {
        pendingSlideRequestRef.current = null;
        targetSlideRef.current = null;
        return;
      }

      targetSlideRef.current = { force, index };

      const swiper = swiperRef.current;

      if (!swiper) {
        pendingSlideRequestRef.current = { force, index };
        return;
      }

      swiper.update?.();

      syncSwiperAtEnd(swiper);

      pendingSlideRequestRef.current = null;

      const visibleSlidesCount = getVisibleSlidesCount(swiper);
      const firstVisibleIndex = swiper.activeIndex ?? 0;
      const lastVisibleIndex = firstVisibleIndex + Math.max(visibleSlidesCount - 1, 0);

      if (!force && index >= firstVisibleIndex && index <= lastVisibleIndex) {
        return;
      }

      swiper.slideTo?.(index, 0, false);
    },
    [syncSwiperAtEnd],
  );

  const onSwiper = useCallback(
    swiper => {
      if (!swiper) {
        return;
      }

      swiperRef.current = swiper;

      syncSwiperAtEnd(swiper);
      swiper.on('resize', syncSwiperAtEnd);
      swiper.on('slideChange', syncSwiperAtEnd);
      swiper.on('slideChangeTransitionEnd', syncSwiperAtEnd);

      const request = pendingSlideRequestRef.current ?? targetSlideRef.current;
      if (request != null) {
        pendingSlideRequestRef.current = null;
        scrollToSlide(request.index, { force: request.force });
      }
    },
    [scrollToSlide, syncSwiperAtEnd],
  );

  return { atEnd, onSwiper, scrollToSlide, syncSwiperAtEnd };
};

export default useBonusSliderAtEnd;
